import { fetchV2WithAuth, getV2WsCredentials, type WsCredentials } from './apiAuth';
import { isTestModeBypass } from './testMode';
import type { ValidationError } from '../types/csv-upload';
import type { Statement } from '@weinsure/agency-statements';
import type {
  ListNotificationsParams,
  NotificationPage,
  NotificationSummary,
  PushPlatform,
} from '../types/notifications';

const API_BASE_URL = import.meta.env.VITE_FRANCHISE_API_URL;

// Put the caller's credentials in the handshake's query string, which is the only place a
// browser can put them: the WebSocket API has no way to send a header on an upgrade. $connect
// runs a Lambda authorizer over them and refuses the socket outright without a valid one
// (WIPE-1452) — every route on that API used to accept anyone.
function wsUrlWithCredentials(wsUrl: string, credentials: WsCredentials): string {
  const url = new URL(wsUrl);
  for (const [name, value] of Object.entries(credentials)) {
    if (value) url.searchParams.set(name, value);
  }
  return url.toString();
}

// Thrown by write() when the API rejects a request with a structured validation
// payload ({ errors: { general?, fields? } }). Admin dialogs catch this to render
// each field message inline next to its input, falling back to `message`
// (the general error) for a banner.
// `status` and `body` are the raw response, for an endpoint that says more than which fields were
// wrong — e.g. the agency redirect API's `existing` rule on a 409.
export class FranchiseValidationError extends Error {
  fields: Record<string, string>;
  status?: number;
  body?: Record<string, unknown>;
  constructor(message: string, fields: Record<string, string>, status?: number, body?: Record<string, unknown>) {
    super(message);
    this.name = 'FranchiseValidationError';
    this.fields = fields;
    this.status = status;
    this.body = body;
  }
}

class FranchiseApiClient {
  private baseUrl: string;
  private cache: Map<string, { data: unknown; timestamp: number }> = new Map();
  private inflight: Map<string, Promise<unknown>> = new Map();
  private cacheTtl = 5 * 60 * 1000; // 5 minutes

  constructor() {
    if (!API_BASE_URL) {
      console.warn('VITE_FRANCHISE_API_URL not configured.');
    }
    this.baseUrl = API_BASE_URL || '';
  }

  private async request<T>(path: string, retries = 2): Promise<T> {
    if (!this.baseUrl) {
      throw new Error('Franchise API URL not configured');
    }

    for (let attempt = 0; attempt <= retries; attempt++) {
      // Shared helper attaches auth (Okta + Microsoft) + x-api-key and replays once on
      // an expired token before returning here.
      const response = await fetchV2WithAuth(`${this.baseUrl}${path}`, { method: 'GET' });

      if (response.status === 429 && attempt < retries) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        continue;
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API request failed: ${response.status} ${response.statusText}. ${text}`);
      }

      return response.json();
    }

    throw new Error('Max retries exceeded');
  }

  private async cachedRequest<T>(path: string): Promise<T> {
    const cached = this.cache.get(path);
    if (cached && Date.now() - cached.timestamp < this.cacheTtl) {
      if (cached.data === null) throw new Error('Request previously failed (rate limited)');
      return cached.data as T;
    }

    const existing = this.inflight.get(path);
    if (existing) return existing as Promise<T>;

    const promise = this.request<T>(path).then(data => {
      this.cache.set(path, { data, timestamp: Date.now() });
      this.inflight.delete(path);
      return data;
    }).catch(err => {
      this.cache.set(path, { data: null, timestamp: Date.now() - this.cacheTtl + 30000 });
      this.inflight.delete(path);
      throw err;
    });

    this.inflight.set(path, promise);
    return promise;
  }

  // Write helper for POST/PUT/DELETE. Sends both x-api-key and the Okta Bearer
  // token (required by system_admin-gated endpoints). Returns undefined for 204.
  private async write<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
    if (!this.baseUrl) {
      throw new Error('Franchise API URL not configured');
    }
    const response = await fetchV2WithAuth(`${this.baseUrl}${path}`, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      // The backend always responds with a JSON error body.
      const body = await response.json();

      // Handle structured validation errors array: { errors: [{ row, column, message }] }
      if (body?.errors && Array.isArray(body.errors)) {
        const message = `Found ${body.errors.length} validation error(s)`;
        throw new FranchiseValidationError(message, { errors: body.errors });
      }

      // Handle structured shape: { errors: { general?, fields? } }.
      if (body?.errors && typeof body.errors === 'object') {
        const fields: Record<string, string> = body.errors.fields ?? {};
        // Surface a sensible single-line message: the general error, the first
        // field message, or a status fallback.
        const message = body.errors.general ?? Object.values(fields)[0] ?? `Request failed: ${response.status}`;
        throw new FranchiseValidationError(message, fields, response.status, body);
      }

      throw new Error(body?.error || `Request failed: ${response.status}`);
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  // Carriers

  // The signed-in franchise owner's own onboarding (WIPE-1645). `onboarding` is null for anyone
  // who isn't a candidate. Uncached: the page is opened to see what has moved.
  async getMyOnboarding(): Promise<MyOnboardingResponse> {
    return this.request('/v2/me/onboarding');
  }

  // The owner's upcoming onboarding meetings as a calendar file, for "Add to calendar". Fetched
  // rather than linked: a plain <a href> can't carry the auth headers.
  async getMyOnboardingIcs(): Promise<Blob> {
    const response = await fetchV2WithAuth(`${this.baseUrl}/v2/me/onboarding/meetings.ics`, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  }

  // Whether the owner can message staff on their carrier onboarding ticket yet (WIPE-1859). Its own
  // call: it asks HubSpot and Jira, which the tracker shouldn't wait on.
  async getMyOnboardingMessages(): Promise<MyOnboardingMessagesResponse> {
    return this.request('/v2/me/onboarding/messages');
  }

  // Posts the message as a comment on the ticket. The ticket is the backend's to resolve, not ours.
  async sendMyOnboardingMessage(payload: MyOnboardingMessageInput): Promise<MyOnboardingMessageSent> {
    return this.write<MyOnboardingMessageSent>('POST', '/v2/me/onboarding/messages', payload);
  }

  async getCarriers(): Promise<FranchiseCarriersResponse> {
    return this.cachedRequest('/v2/carriers');
  }

  async getCarrierById(carrierId: string): Promise<FranchiseCarrierDetailResponse> {
    return this.request(`/v2/carriers/${carrierId}`);
  }

  async getCarrierContacts(carrierId: string): Promise<FranchiseContactsResponse> {
    return this.request(`/v2/carriers/${carrierId}/contacts`);
  }

  async updateCarrierNotes(carrierId: string, notes: string): Promise<void> {
    if (!this.baseUrl) throw new Error('Franchise API URL not configured');
    const response = await fetchV2WithAuth(`${this.baseUrl}/v2/carriers/${carrierId}`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to update carrier: ${response.status}. ${text}`);
    }
  }

  // Agencies

  async lookupByEmail(email: string): Promise<FranchiseLookupResponse | null> {
    if (!email || !email.includes('@') || email.endsWith('@localhost')) return null;

    // No test-mode identity exists in any environment's database, so this can only 404 —
    // and the negative cache below is per-instance, so it was re-requested on every page
    // load. "No agency" is the same answer, without the round trip (WIPE-1190).
    if (isTestModeBypass()) return null;

    const path = `/v2/lookup?email=${encodeURIComponent(email)}`;
    const cached = this.cache.get(path);
    if (cached && Date.now() - cached.timestamp < this.cacheTtl) {
      return cached.data as FranchiseLookupResponse | null;
    }

    try {
      if (!this.baseUrl) return null;
      const response = await fetchV2WithAuth(`${this.baseUrl}${path}`, { method: 'GET' });
      if (response.status === 404 || response.status === 400) {
        this.cache.set(path, { data: null, timestamp: Date.now() });
        return null;
      }
      if (!response.ok) return null;
      const data = await response.json();
      this.cache.set(path, { data, timestamp: Date.now() });
      return data;
    } catch (error) {
      console.error('Error looking up by email:', error);
      throw error;
    }
  }

  async getAgencies(): Promise<FranchiseAgenciesResponse> {
    return this.cachedRequest('/v2/branches');
  }

  // A branch's statements, newest period first. The API allows accounting and admins any branch, and an
  // agency-statements grant holder their own (403 otherwise).
  async getBranchStatements(branchId: string): Promise<AgencyStatementsResponse> {
    return this.request(`/v2/branches/${branchId}/statements`);
  }

  // One statement as its PDF shows it. Same access as getBranchStatements, checked against the
  // statement's own branch.
  async getStatement(statementId: string): Promise<Statement> {
    const res = await this.request<{ data: { statement: Statement } }>(`/v2/statements/${statementId}`);
    return res.data.statement;
  }

  // The agency's payment summary for a period; the fees view reads its subscriptions. Never cached, so a
  // fee change accounting has just applied shows at once. Same access as getBranchStatements.
  async getAgencySummary(branchId: string, period: string): Promise<AgencySummary> {
    const query = new URLSearchParams({ branch_id: branchId, period });
    const res = await this.request<{ data: AgencySummary }>(`/v2/agencySummary?${query}`);
    return res.data;
  }

  // The agency's open fee change requests. Never cached: a request accounting has just applied or
  // cancelled must stop showing as pending.
  async getPendingFeeChangeRequests(branchId: string): Promise<FeeChangeRequest[]> {
    const query = new URLSearchParams({ branch_id: branchId, status: 'pending' });
    const res = await this.request<{ requests: FeeChangeRequest[] }>(`/v2/subscriptionChangeRequests?${query}`);
    return res.requests;
  }

  // Ask accounting to change the agency's quantity for a service; 0 removes it. Throws
  // FranchiseValidationError with the field at fault on a 400, and with status 409 when the service
  // already has an open request.
  async requestFeeChange(body: FeeChangeRequestPayload): Promise<FeeChangeRequest> {
    const res = await this.write<{ data: FeeChangeRequest }>('POST', '/v2/subscriptionChangeRequests', body);
    return res.data;
  }

  // Every service an agency can be charged for.
  async getServiceTypes(): Promise<ServiceType[]> {
    const res = await this.cachedRequest<{ service_types: ServiceType[] }>('/v2/serviceTypes');
    return res.service_types;
  }

  // A short-lived presigned URL for an attachment, such as a statement's PDF.
  async getAttachmentDownloadUrl(attachmentId: string): Promise<string> {
    const res = await this.request<{ data: { download_url: string; expires_in: number } }>(
      `/v2/attachments/${attachmentId}/download`
    );
    return res.data.download_url;
  }

  // A short-lived SharePoint URL for the producer statement of a statement's branch and month.
  async getProducerStatementDownloadUrl(statementId: string): Promise<string> {
    const res = await this.request<{ data: { download_url: string; filename: string } }>(
      `/v2/statements/${statementId}/producer-statement`
    );
    return res.data.download_url;
  }

  // Update the branch fields that are NOT synced from HubSpot (WIPE-919): office_hours, email and
  // google_conversion_id. Admin-only server-side.
  // Throws FranchiseValidationError on a 400 so the editor can render inline field errors.
  async updateBranch(id: string, body: BranchUpdatePayload): Promise<FranchiseAgency> {
    const res = await this.write<{ branch: FranchiseAgency }>('PUT', `/v2/branches/${id}`, body);
    return res.branch;
  }

  // Choose whose headshot represents the branch in the agent directory, or null for the default
  // pick (WIPE-1894). Admin-only server-side; the contact must
  // be one of the branch's. Drops the cached branch reads so this session sees the change.
  async setAgencyPhoto(branchId: string, hubspotId: string | null): Promise<string | null> {
    const res = await this.write<{ agency_photo_hubspot_id: string | null }>(
      'PUT', `/v2/branches/${branchId}/agency-photo`, { hubspot_id: hubspotId }
    );
    this.cache.delete('/v2/branches');
    this.cache.delete('/v2/branches/directory');
    return res.agency_photo_hubspot_id;
  }

  // Open branches each with a single precomputed avatar_url, in one request — for the
  // agent directory. Replaces the old per-branch contacts fan-out (1 + N requests).
  async getDirectory(): Promise<FranchiseDirectoryResponse> {
    return this.cachedRequest('/v2/branches/directory');
  }

  // The whole branch, FEIN included. The API answers only the branch's own people and 404s anyone
  // else, admins included (WIPE-1833).
  async getBranchDetails(id: string): Promise<{ branch: FranchiseBranchDetails }> {
    return this.request(`/v2/branches/${id}/details`);
  }

  async getBranchContacts(id: string): Promise<FranchiseContactsResponse> {
    return this.request(`/v2/branches/${id}/contacts`);
  }

  // Every active contact across all open branches, each tagged with its branch, in one
  // request — for the admin contact picker (replaces a per-branch fan-out).
  async getAllBranchContacts(): Promise<FranchiseAllContactsResponse> {
    return this.cachedRequest('/v2/branches/contacts');
  }

  async getAgencyAppointments(id: string): Promise<FranchiseAppointmentsResponse> {
    return this.request(`/v2/branches/${id}/appointments`);
  }

  // Kudos

  async getKudos(limit = 50): Promise<KudosListResponse> {
    const qs = new URLSearchParams({ limit: String(limit) });
    return this.request(`/v2/kudos?${qs.toString()}`);
  }

  async submitKudos(payload: KudosCreatePayload): Promise<KudosEntry> {
    return this.write<KudosEntry>('POST', '/v2/kudos', payload);
  }

  // Commercial certificates (COI)

  // Submit a certificate request (WIPE-1514). The backend relays it to InsuredMine and emails the
  // caller a receipt; this page used to embed InsuredMine's form directly, which acknowledged
  // nothing. A 201 means InsuredMine holds the request — anything else means it does not, so a
  // thrown error here is safe to present as "not submitted, try again".
  async submitCommercialCertificate(
    payload: CommercialCertificatePayload
  ): Promise<CommercialCertificateResult> {
    return this.write<CommercialCertificateResult>('POST', '/v2/commercial-certificates', payload);
  }

  // Mortgage/lienholder requests

  // Submit a mortgagee and/or lienholder request (WIPE-1199). The backend relays it to InsuredMine
  // with the AgencyOS lead source and emails the caller a receipt. Without a file, a resolved result
  // means InsuredMine holds it and a thrown error means it does not. With a file (uploaded first with
  // uploadAttachment), the result is `queued`: the file is scanned and the request relayed from a job,
  // and the receipt, or an email saying it was not submitted, follows from there.
  async submitMortgageLienholderRequest(
    payload: MortgageLienholderPayload
  ): Promise<MortgageLienholderResult> {
    return this.write<MortgageLienholderResult>('POST', '/v2/mortgage-lienholder-requests', payload);
  }

  // Headshots
  //
  // Two-step upload: presign a PUT to the headshots bucket, PUT the file directly to S3, then
  // persist the resulting public URL onto the contact (all rows sharing the hubspot_id). The
  // S3 key is a random UUID, so each upload yields a fresh URL that can't be served stale.

  async getHeadshotUploadUrl(hubspotId: string, contentType: string): Promise<HeadshotUploadUrlResponse> {
    return this.write<HeadshotUploadUrlResponse>('POST', '/v2/headshots/upload-url', {
      hubspot_id: hubspotId,
      content_type: contentType,
    });
  }

  async setHeadshotUrl(hubspotId: string, url: string): Promise<{ avatar_url: string; updated: number }> {
    return this.write('PUT', '/v2/headshots', { hubspot_id: hubspotId, url });
  }

  // Upload a headshot end to end and return its public URL. The presigned PUT signs only
  // Content-Type, so the PUT must send a byte-identical Content-Type header (or S3 403s).
  async uploadHeadshot(hubspotId: string, file: File): Promise<string> {
    const { upload_url, public_url } = await this.getHeadshotUploadUrl(hubspotId, file.type);

    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload headshot');
    }

    await this.setHeadshotUrl(hubspotId, public_url);
    return public_url;
  }

  // Branches

  async getBranches(): Promise<FranchiseBranchesResponse> {
    return this.request('/v2/branches');
  }

  // Admin data viewer (system_admin only). Tables and columns are discovered live
  // from information_schema by the backend, so this stays dynamic.

  async getAdminTables(): Promise<AdminTablesResponse> {
    return this.request('/v2/adminData/tables');
  }

  // Server-side paginated/sorted/filtered rows for one table. Built from Material
  // React Table's state. Uncached: the query string changes on every interaction.
  async getAdminRows(params: AdminRowsParams): Promise<AdminRowsResponse> {
    const qs = new URLSearchParams();
    qs.set('table', params.table);
    qs.set('page', String(params.page));
    qs.set('pageSize', String(params.pageSize));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.globalFilter) qs.set('globalFilter', params.globalFilter);
    if (params.filters && params.filters.length) {
      qs.set('filters', JSON.stringify(params.filters));
    }
    return this.request(`/v2/adminData/rows?${qs.toString()}`);
  }

  // Event log (WIPE-971). One append-only table of typed entries; the backend's
  // registry describes each type, so the viewer needs no per-type code.

  // The log-type registry, with each type's current entry count. Drives the
  // viewer's type picker, columns, and type-specific filters.
  async getLogTypes(): Promise<LogTypesResponse> {
    return this.request('/v2/logs/types');
  }

  // One filtered, paginated page of entries for a single log type. Uncached: the
  // query string changes on every interaction.
  async getLogEntries(params: LogEntriesParams): Promise<LogEntriesResponse> {
    const qs = new URLSearchParams();
    qs.set('log_type', params.logType);
    qs.set('page', String(params.page));
    qs.set('pageSize', String(params.pageSize));
    if (params.sort) qs.set('sort', params.sort);
    if (params.dir) qs.set('dir', params.dir);
    if (params.branchId) qs.set('branch_id', params.branchId);
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    if (params.startDate) qs.set('start_date', params.startDate);
    if (params.endDate) qs.set('end_date', params.endDate);
    // Type-specific filters over the JSONB payload. Bracket notation so Express's
    // extended query parser rebuilds them as a `data` object, which the backend
    // turns into a single jsonb containment predicate.
    for (const [key, value] of Object.entries(params.data ?? {})) {
      if (value) qs.set(`data[${key}]`, value);
    }
    return this.request(`/v2/logs/entries?${qs.toString()}`);
  }

  // Documents (admin: PDF upload -> Docling markdown + Bedrock structured extraction)

  async listDocuments(): Promise<FranchiseDocument[]> {
    const res = await this.request<{ data: FranchiseDocument[] }>('/v2/documents');
    return res.data;
  }

  async getDocument(id: string): Promise<FranchiseDocument> {
    const res = await this.request<{ data: FranchiseDocument }>(`/v2/documents/${id}`);
    return res.data;
  }

  // Create a document from an already-uploaded attachment + a type. Kicks off processing and
  // returns both the document and the jobId to subscribe to for progress.
  async createDocument(body: DocumentCreatePayload): Promise<{ document: FranchiseDocument; jobId: string }> {
    const res = await this.write<{ data: FranchiseDocument; jobId: string }>('POST', '/v2/documents', body);
    return { document: res.data, jobId: res.jobId };
  }

  // Re-run extraction for an existing document. Returns the new jobId.
  async processDocument(id: string): Promise<{ jobId: string }> {
    const res = await this.write<{ data: { jobId: string } }>('POST', `/v2/documents/${id}/process`);
    return res.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await this.write<void>('DELETE', `/v2/documents/${id}`);
  }

  // Document Types (admin: named extraction schemas)

  async listDocumentTypes(): Promise<FranchiseDocumentType[]> {
    const res = await this.request<{ data: FranchiseDocumentType[] }>('/v2/documentTypes');
    return res.data;
  }

  async getDocumentType(id: string): Promise<FranchiseDocumentType> {
    const res = await this.request<{ data: FranchiseDocumentType }>(`/v2/documentTypes/${id}`);
    return res.data;
  }

  async createDocumentType(body: DocumentTypePayload): Promise<FranchiseDocumentType> {
    const res = await this.write<{ data: FranchiseDocumentType }>('POST', '/v2/documentTypes', body);
    return res.data;
  }

  async updateDocumentType(id: string, body: DocumentTypePayload): Promise<FranchiseDocumentType> {
    const res = await this.write<{ data: FranchiseDocumentType }>('PUT', `/v2/documentTypes/${id}`, body);
    return res.data;
  }

  async deleteDocumentType(id: string): Promise<void> {
    await this.write<void>('DELETE', `/v2/documentTypes/${id}`);
  }

  // Agency URL redirects on the marketing site (WIPE-1760). Uncached: the list is what the tool edits.

  async listAgencyRedirects(): Promise<AgencyRedirect[]> {
    const res = await this.request<{ data: AgencyRedirect[] }>('/v2/agency-redirects/rules');
    return res.data;
  }

  async listAgencyRedirectDestinations(): Promise<AgencyRedirectDestination[]> {
    const res = await this.request<{ data: AgencyRedirectDestination[] }>('/v2/agency-redirects/destinations');
    return res.data;
  }

  async createAgencyRedirect(body: AgencyRedirectPayload): Promise<AgencyRedirect> {
    const res = await this.write<{ data: AgencyRedirect }>('POST', '/v2/agency-redirects/rules', body);
    return res.data;
  }

  async updateAgencyRedirect(id: string, body: AgencyRedirectPayload): Promise<AgencyRedirect> {
    const res = await this.write<{ data: AgencyRedirect }>('PUT', `/v2/agency-redirects/rules/${id}`, body);
    return res.data;
  }

  async deleteAgencyRedirect(id: string): Promise<void> {
    await this.write<void>('DELETE', `/v2/agency-redirects/rules/${id}`);
  }

  // Zoom call capture (WIPE-1395)

  // Not cached: the page renders a consent state the user is about to change, and a stale read
  // would show a checkbox that disagrees with what is actually configured.
  async getCallCapture(): Promise<CallCaptureSettings> {
    return this.request('/v2/me/call-capture');
  }

  async setCallCaptureEnabled(groupId: string, enabled: boolean): Promise<void> {
    await this.write<void>('PUT', `/v2/me/call-capture/${groupId}`, { enabled });
  }

  // The authorize URL is built by the backend, not here: the client id, the scope list and the
  // redirect URI have to match the Zoom app exactly, and a second copy in frontend code is a copy
  // that will drift.
  async getZoomAuthorizeUrl(): Promise<string> {
    const res = await this.request<{ url: string }>('/v2/me/call-capture/zoom/authorize');
    return res.url;
  }

  // Posted with the caller's normal bearer token, which is what ties the install to a verified
  // identity — Zoom's redirect carries no identity we could trust.
  async connectZoom(code: string, state: string): Promise<void> {
    await this.write<void>('POST', '/v2/me/call-capture/zoom', { code, state });
  }

  async disconnectZoom(): Promise<void> {
    await this.write<void>('DELETE', '/v2/me/call-capture/zoom');
  }

  async deleteCapturedCall(callId: string): Promise<void> {
    await this.write<void>('DELETE', `/v2/me/call-capture/calls/${callId}`);
  }

  // Notifications. Never cached: the bell and the inbox exist to show a read the user has just made.
  //
  // The test-mode identity holds no bearer token, so these reads could only 401 against the real API
  // the E2E build points at, once per page load (WIPE-1190's 4xx alarm). An empty inbox is the honest
  // answer for an identity no notification can be addressed to, and it keeps the bell hidden.

  async getNotificationSummary(): Promise<NotificationSummary> {
    if (isTestModeBypass()) return { unread: 0, total: 0 };
    return this.request('/v2/me/notifications/summary');
  }

  // One page, newest first. Pass the previous page's next_cursor as `before` for the next one.
  async listNotifications(params: ListNotificationsParams = {}): Promise<NotificationPage> {
    if (isTestModeBypass()) return { data: [], next_cursor: null };
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.before) qs.set('before', params.before);
    if (params.unread) qs.set('unread', 'true');
    const query = qs.toString();
    return this.request(`/v2/me/notifications${query ? `?${query}` : ''}`);
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.write<void>('POST', `/v2/me/notifications/${encodeURIComponent(id)}/read`);
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.write<void>('POST', '/v2/me/notifications/read-all');
  }

  // Ties this device's FCM token to the caller. The backend moves a token already held by someone
  // else, so whoever last signed in on a device is who it notifies. 403 while viewing as another user.
  async registerPushToken(token: string, platform: PushPlatform): Promise<void> {
    await this.write<void>('POST', '/v2/me/push-tokens', { token, platform });
  }

  async unregisterPushToken(token: string, platform: PushPlatform): Promise<void> {
    await this.write<void>('DELETE', '/v2/me/push-tokens', { token, platform });
  }

  // Attachments

  async createAttachment(body: AttachmentCreatePayload): Promise<AttachmentCreateResponse> {
    const res = await this.write<{ data: AttachmentCreateResponse }>('POST', '/v2/attachments', body);
    return res.data;
  }

  async confirmAttachment(attachmentId: string): Promise<void> {
    await this.write<void>('POST', `/v2/attachments/${attachmentId}/confirm`);
  }

  // Open a WebSocket, subscribe to a jobId, and stream JobMessage updates to the
  // caller. Returns a cleanup function that closes the socket. The server replays
  // the current job state on subscribe, so this works even if the job already
  // finished.
  async subscribeToJob(jobId: string, onMessage: (msg: JobMessage) => void): Promise<() => void> {
    const wsUrl = import.meta.env.VITE_FRANCHISE_WS_URL;
    if (!wsUrl) {
      console.warn('VITE_FRANCHISE_WS_URL not configured.');
      return () => {};
    }

    const credentials = await getV2WsCredentials();
    const socket = new WebSocket(wsUrlWithCredentials(wsUrl, credentials));

    socket.onopen = () => {
      socket.send(JSON.stringify({ action: 'subscribe', jobId, ...credentials }));
    };

    socket.onmessage = (event) => {
      try {
        onMessage(JSON.parse(event.data) as JobMessage);
      } catch {
        // ignore malformed frames
      }
    };

    return () => socket.close();
  }

  // Open a WebSocket connection and wait for it to be ready, then return a
  // subscribe function. This ensures the connection is established before a job
  // starts, preventing the race where fast jobs complete before the socket opens.
  //
  // One connection can carry any number of jobs: the frame handler is registered
  // once here and each frame is dispatched by its own jobId. It has to be done
  // that way round — a handler assigned per subscribe overwrites the previous
  // one, so watching several jobs at once would silently deliver every frame to
  // whichever job subscribed last while the rest sat at 0%.
  //
  // `onUnexpectedClose` fires when the connection drops without close() having
  // been called — an API Gateway WebSocket idles out at 10 minutes, which a wide
  // batch of documents can outlast. The caller's recovery is its own: re-read the
  // authoritative state over HTTP and re-subscribe to whatever is still running.
  async prepareJobSubscription(
    options: { onUnexpectedClose?: () => void } = {}
  ): Promise<{
    subscribe: (
      jobId: string,
      onMessage: (msg: JobMessage) => void,
      onDraft?: (draft: JobDraftMessage) => void
    ) => void;
    close: () => void;
  }> {
    const wsUrl = import.meta.env.VITE_FRANCHISE_WS_URL;
    if (!wsUrl) {
      console.warn('VITE_FRANCHISE_WS_URL not configured.');
      return {
        subscribe: () => {},
        close: () => {},
      };
    }

    const open = async (forceRefresh: boolean) => {
      const credentials = await getV2WsCredentials(forceRefresh);
      const socket = new WebSocket(wsUrlWithCredentials(wsUrl, credentials));

      // Wait for the socket to open
      await new Promise<void>((resolve, reject) => {
        socket.onopen = () => resolve();
        // Also how a refused handshake surfaces: the browser reports a 401 from the upgrade as
        // an error event carrying neither a status nor a body, so an expired token is
        // indistinguishable here from an unreachable endpoint.
        socket.onerror = () => reject(new Error('WebSocket connection failed'));
      });

      return { socket, credentials };
    };

    // Retry once with freshly minted tokens. Because the failure above can't be classified,
    // this is the handshake's version of the replay fetchV2WithAuth performs when the backend
    // reports an expired token: a tab left open past the access token's lifetime would
    // otherwise fail to open a socket, and every job started from it would sit at 0%.
    const { socket, credentials } = await open(false).catch(() => open(true));

    const handlers = new Map<string, (msg: JobMessage) => void>();
    const draftHandlers = new Map<string, (draft: JobDraftMessage) => void>();
    let closedByCaller = false;

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as JobMessage | JobDraftMessage;
        // A draft is not the job's state, so it goes only to a caller that asked for drafts.
        if ('kind' in message && message.kind === 'draft') draftHandlers.get(message.jobId)?.(message);
        else handlers.get(message.jobId)?.(message as JobMessage);
      } catch {
        // ignore malformed frames
      }
    };

    // A failed connection surfaces as a close too, so recovery is reported from one
    // place. The open-time rejection above has already had its chance by now.
    socket.onerror = () => {};
    socket.onclose = () => {
      if (!closedByCaller) options.onUnexpectedClose?.();
    };

    return {
      subscribe: (jobId: string, onMessage: (msg: JobMessage) => void, onDraft?: (draft: JobDraftMessage) => void) => {
        handlers.set(jobId, onMessage);
        if (onDraft) draftHandlers.set(jobId, onDraft);
        // A subscribe arriving after the connection died is not an error worth
        // throwing: onUnexpectedClose has already told the caller to rebuild.
        if (socket.readyState === WebSocket.OPEN) {
          // The credentials again, in the frame. A subscribe reaches the server with neither
          // headers nor a query string — only the connection id — so it re-verifies them to
          // confirm the job is this user's. Being on an authenticated socket isn't enough: the
          // subscribe MOVES the job's connection_id, so subscribing to someone else's job would
          // take its progress stream away from whoever was waiting on it.
          socket.send(JSON.stringify({ action: 'subscribe', jobId, ...credentials }));
        }
      },
      close: () => {
        closedByCaller = true;
        handlers.clear();
        draftHandlers.clear();
        socket.close();
      },
    };
  }

  isConfigured(): boolean {
    return !!this.baseUrl;
  }
}

// Types matching the OpenAPI spec

export interface FranchiseCarrier {
  id: string;
  // Raw HubSpot company id. `id` is now a UUID, so Contentful news/event lookups
  // (keyed on the numeric HubSpot id) must use hubspot_id, not id.
  hubspot_id: string;
  name: string;
  website: string | null;
  carrier_tags: string | null;
  notes: string | null;
  phone: string | null;
  status: string;
}

export interface FranchiseCarrierAppointment {
  hubspot_appointment_id: string;
  setup_name: string;
  agency_code: string;
  agent_name: string | null;
  agent_email: string | null;
  branch_name: string | null;
  branch_code: number | null;
  estimated_complete_date: string | null;
  status_update: string | null;
  created_at: string;
  updated_at: string;
}

export interface FranchiseCarriersResponse {
  carriers: FranchiseCarrier[];
  count: number;
}

export interface FranchiseCarrierDetailResponse {
  carrier: FranchiseCarrier;
  appointments: FranchiseCarrierAppointment[];
  appointment_count: number;
}

export interface FranchiseAgency {
  id: string;
  hubspot_id: string;
  name: string;
  branch_code: number;
  business_entity_name: string;
  dba_name: string;
  agent_in_charge: string;
  reporting_name: string;
  status: string;
  cohort: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  domain: string | null;
  // Full URL; prefer it to `domain`, a bare domain the franchise object doesn't carry.
  website: string | null;
  // Locally-managed fields not sourced from HubSpot (WIPE-919), editable via updateBranch.
  office_hours: string | null;
  email: string | null;
  // Google tag id (e.g. "AW-1234567890") for a branch running its own Google Ads conversion
  // tracking; the marketing site renders the gtag.js loader for it on the branch's agency page
  // (WIPE-1180). Null for the vast majority of branches, which get no branch-specific tag.
  google_conversion_id: string | null;
  // hubspot_id of the contact an admin chose as the branch's Agency Photo (WIPE-1894); null, or
  // absent from an older API, for the default pick.
  agency_photo_hubspot_id?: string | null;
  commission_split: string | null;
  opening_date: string | null;
  // HubSpot owner id of the branch's account manager. The name is resolved by franchiseapi's sync
  // (WIPE-1607); null when the id has no HubSpot owner or the branch has no account manager.
  account_manager: string | null;
  account_manager_name: string | null;
  agency_license: string | null;
  agency_npn: string | null;
  carrier_tags: string | null;
  insuredmine_branch_url: string | null;
  facebook_company_page: string | null;
  has_multiple_locations: boolean;
  // Whether the current (authenticated) caller may edit this branch's public bio. Computed by
  // the backend from abilities per-branch; false for anonymous callers. The frontend gates the
  // bio editor on this instead of deriving it from roles.
  canEdit?: boolean;
}

export interface FranchiseBranchDetails extends FranchiseAgency {
  fein: string | null;
}

export interface FranchiseContact {
  hubspot_id: string;
  firstname: string;
  lastname: string;
  email: string;
  jobtitle: string | null;
  phone: string | null;
  mobilephone: string | null;
  avatar_url: string | null;
  status: string | null;
  hide_staffer: boolean;
}

export interface FranchiseAppointment {
  hubspot_id: string;
  setup_name: string;
  agency_code: string;
  estimated_complete_date: string | null;
  status_update: string | null;
  created_at: string;
}

export interface KudosEntry {
  id: string;
  recipientName: string;
  recipientEmail: string;
  recipientDepartment: string;
  message: string;
  senderName: string;
  senderBranch: string;
  createdAt: string;
}

export interface KudosListResponse {
  kudos: KudosEntry[];
  count: number;
}

export interface KudosCreatePayload {
  recipientName: string;
  recipientEmail?: string;
  recipientDepartment?: string;
  message: string;
  senderName?: string;
  senderBranch?: string;
}

// A file picked on the COI form, in the form.io "base64 storage" shape InsuredMine's own form posts.
// The backend passes it through untouched, so the shape is InsuredMine's rather than ours.
export interface CommercialCertificateAttachment {
  storage: 'base64';
  name: string;
  originalName: string;
  url: string; // data:<mime>;base64,<...>
  size: number;
  type: string;
}

export interface CommercialCertificatePayload {
  policyNumber: string;
  insuredName: string;
  // One free-text name-and-full-address block per certificate holder.
  certificateHolders: string[];
  endorsements: {
    additionalInsured: boolean;
    waiverOfSubrogation: boolean;
    primaryAndNonContributory: boolean;
    thirtyDayNoticeOfCancellation: boolean;
  };
  additionalInformationNotes?: string;
  contactIfMoreInformationNeeded: 'agent' | 'insured';
  attachment?: CommercialCertificateAttachment | null;
  originUrl?: string;
}

export interface CommercialCertificateResult {
  success: boolean;
  submittedAt: string;
  // Whether the receipt email was sent. False does not undo the submission — the request is filed
  // either way, so the form must not invite a resubmission on the strength of it.
  confirmationSent: boolean;
}

export interface MortgageeRequestDetails {
  fullName: string;
  clause: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  loanNumber: string;
  position: string;
  isEscrowed: 'yes' | 'no';
  requestorName: string;
  requestorEmail?: string;
  requestorFax?: string;
  comments: string;
}

export interface LienholderRequestDetails {
  fullName: string;
  clause: string;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  vin: string;
  loanNumber: string;
  requestorName: string;
  requestorEmail?: string;
  requestorFax?: string;
  comments: string;
}

export interface MortgageLienholderPayload {
  isMortgagee: boolean;
  isLienholder: boolean;
  insuredFullName: string;
  policyNumber: string;
  insuredStreet: string;
  insuredApt?: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  // Present only for the section(s) the agent chose.
  mortgagee?: MortgageeRequestDetails;
  lienholder?: LienholderRequestDetails;
  // An attachment the agent uploaded with uploadAttachment.
  attachmentId?: string;
  originUrl?: string;
}

// A request without a file: relayed already. confirmationSent false does not undo the submission.
export interface MortgageLienholderFiledResult extends CommercialCertificateResult {
  queued?: false;
}

// A request with a file: received, and relayed once the file clears its malware scan.
export interface MortgageLienholderQueuedResult {
  success: boolean;
  queued: true;
  jobId: string;
  submittedAt: string;
}

export type MortgageLienholderResult = MortgageLienholderFiledResult | MortgageLienholderQueuedResult;

export interface FranchiseLookupResponse {
  id: string;
  matched_on: 'agency' | 'contact';
}

export interface HeadshotUploadUrlResponse {
  upload_url: string;
  public_url: string;
  key: string;
  expires_in: number;
}

// One band of what a subscription bills: a tier, or the agency's own flat rate.
export interface AgencyTierCharge {
  quantity: number;
  rate: number;
  amount: number;
  label: string | null;
}

// A subscription billed against the agency in the period.
export interface AgencySubscription {
  id: string;
  service_type_id: string;
  service_type_name: string;
  billing_rule: 'per_user' | 'per_branch' | 'flat_rate' | 'usage_based';
  subject: 'user' | 'branch';
  quantity: number;
  // The agency's own flat price, or null when it is billed from the service type's tiers.
  custom_rate: number | null;
  start_date: string;
  end_date: string | null;
  charges: AgencyTierCharge[];
  monthly_amount: number;
  charged: boolean;
}

export interface ServiceType {
  id: string;
  name: string;
}

// A request for accounting to change an agency's quantity of one service, the parts this app reads.
export interface FeeChangeRequest {
  id: string;
  branch_id: string;
  service_type_id: string;
  current_quantity: number | null;
  requested_quantity: number;
  status: 'pending' | 'completed' | 'cancelled';
  note: string | null;
  requester_name: string | null;
  created_at: string;
  service_type?: { id: string; name: string };
}

export interface FeeChangeRequestPayload {
  branch_id: string;
  service_type_id: string;
  requested_quantity: number;
  note?: string | null;
}

// The parts of GET /v2/agencySummary this app reads: the subscriptions billed in the period.
export interface AgencySummary {
  period: string;
  subscriptions: AgencySubscription[];
}

export interface AgencyStatementsResponse {
  statements: Statement[];
  count: number;
}

export interface FranchiseAgenciesResponse {
  branches: FranchiseAgency[];
  count: number;
}

// Editable non-HubSpot branch fields (WIPE-919). Only keys present are updated; null clears a value.
export interface BranchUpdatePayload {
  office_hours?: string | null;
  email?: string | null;
  google_conversion_id?: string | null;
}

export interface FranchiseDirectoryResponse {
  branches: (FranchiseAgency & { avatar_url: string | null })[];
  count: number;
}

export interface FranchiseContactsResponse {
  contacts: FranchiseContact[];
  count: number;
}

// Contact rows joined to a minimal branch, from GET /v2/branches/contacts.
export interface FranchiseContactWithBranch extends FranchiseContact {
  branch: { id: string; name: string; dba_name: string; branch_code: number } | null;
}

export interface FranchiseAllContactsResponse {
  contacts: FranchiseContactWithBranch[];
  count: number;
}

export interface FranchiseAppointmentsResponse {
  appointments: FranchiseAppointment[];
  count: number;
}

// The full branch list (/branches). Only the fields used by admin dropdowns are
// typed here; the endpoint returns many more.
export interface FranchiseBranch {
  id: string;
  name: string;
  branch_code: number;
  status: string;
}

// A 301 from weinsuregroup.com/agency/<source_slug> to destination_path (a site path: '/' or
// /agency/<slug>).
export interface AgencyRedirect {
  id: string;
  source_slug: string;
  destination_path: string;
  note: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgencyRedirectDestination {
  path: string;
  name: string;
}

// source and destination may each be a full URL, a path or a slug; the API normalises them. `force`
// redirects a URL that still serves a live agency page, after the API has warned about it.
export interface AgencyRedirectPayload {
  source: string;
  destination: string;
  note?: string | null;
  force?: boolean;
}

export interface FranchiseBranchesResponse {
  branches: FranchiseBranch[];
  count: number;
}

// Admin data viewer types — mirror the backend's information_schema introspection.

export interface AdminColumnMeta {
  name: string;
  dataType: string;
  isDate: boolean;
  isBranch: boolean;
}

export interface AdminTableMeta {
  name: string;
  columns: AdminColumnMeta[];
}

export interface AdminTablesResponse {
  tables: AdminTableMeta[];
  count: number;
}

export interface AdminRowsParams {
  table: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  globalFilter?: string;
  // Material React Table's column-filter state, passed through verbatim. Each
  // value is either a scalar or a [from, to] range; the backend validates `id`
  // against the schema and picks the operator from the value's shape.
  filters?: { id: string; value: unknown }[];
}

export interface AdminRowsResponse {
  rows: Record<string, unknown>[];
  rowCount: number;
}

// Event log types (WIPE-971) — mirror the backend's log-type registry
// (franchiseapi src/lib/logTypes.ts). Everything type-specific about the viewer
// comes from here at runtime, so a new log type needs no frontend change.

export type LogEntryStatus = 'success' | 'failure' | 'warning' | 'info';

export interface LogTypeColumn {
  // Key within the entry's `data` object.
  path: string;
  label: string;
}

export interface LogTypeFilter {
  path: string;
  label: string;
  // Present means render a select of these values instead of a text input.
  options?: string[];
}

export interface LogTypeMeta {
  key: string;
  label: string;
  description: string;
  columns: LogTypeColumn[];
  filters: LogTypeFilter[];
  allowedKeys: string[];
  entryCount: number;
}

export interface LogTypesResponse {
  types: LogTypeMeta[];
}

export interface LogEntriesParams {
  logType: string;
  page: number;
  pageSize: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  branchId?: string;
  status?: LogEntryStatus;
  // Free-text search across summary and subject.
  q?: string;
  // ISO timestamps bounding occurred_at.
  startDate?: string;
  endDate?: string;
  // Type-specific filters, keyed by `data` path.
  data?: Record<string, string>;
}

export interface LogEntryRow {
  id: string;
  log_type: string;
  occurred_at: string;
  branch_id: string | null;
  // Raw code as the emitter sent it. Kept even when branch_id is null, so a
  // misrouted event stays visible instead of showing as blank.
  branch_code: string | null;
  branch_name: string | null;
  status: LogEntryStatus | null;
  summary: string;
  subject: string | null;
  actor: string | null;
  correlation_id: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export interface LogEntriesResponse {
  rows: LogEntryRow[];
  rowCount: number;
}

// Attachments

export interface AttachmentCreatePayload {
  filename: string;
  content_type: string;
  file_size: number;
}

export interface AttachmentCreateResponse {
  attachment_id: string;
  upload_url: string;
  expires_in: number;
  upload_status: string;
}

// CSV Upload

// Terminal result payload for a successful upload job (Job.result).
// Re-export from shared types for backward compatibility
export type { CsvUploadResult } from '../types/csv-upload';

// Alias for backward compatibility
export type JobValidationError = ValidationError;

// A job progress/terminal message streamed over the WebSocket. `result` is set
// on success; `errors` is either a validation-errors array ({ errors: [...] })
// or a general { message } on failure.
//
// For a new async job: add its enqueue method to this client, then drive it with
// useJobTrigger (click-triggered) or useJobSubscription (the shared core) and cast
// `result` to that job's own terminal payload.
export interface JobMessage {
  jobId: string;
  method: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  progress: number;
  phase?: string | null;
  // Monotonic per-job sequence (see backend jobProgress). The subscription hook
  // discards any message whose seq is not greater than the highest already seen,
  // so a stale snapshot pushed out of order can't roll the UI backwards.
  seq: number;
  result?: unknown;  // Job-specific result type - cast by the consuming hook
  errors?: { errors?: ValidationError[]; message?: string };
}

// A job's work in progress, sent between its job messages by a job that has one (the assistant's
// answer being written). Not job state: it carries no status or seq, and is never replayed, so a client
// that misses one just waits for the next. `text` is the whole draft so far, not a delta. `draftSeq`
// rises with each frame; a frame whose draftSeq is not above the last one seen is stale.
export interface JobDraftMessage {
  jobId: string;
  kind: 'draft';
  draftSeq: number;
  activity: string;
  text: string;
}

// Documents feature

export type DocumentStatus = 'pending' | 'processing' | 'extracted' | 'failed';

export interface FranchiseDocumentType {
  id: string;
  name: string;
  description: string | null;
  // JSON Schema describing the structured extraction shape.
  schema: unknown;
  model: string;
  prompt: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface DocumentTypePayload {
  name: string;
  description?: string | null;
  schema: unknown;
  model?: string;
  prompt?: string | null;
}

export interface FranchiseDocument {
  id: string;
  name: string;
  attachment_id: string;
  document_type_id: string;
  status: DocumentStatus;
  job_id: string | null;
  markdown: string | null;
  extraction: unknown;
  error: string | null;
  created_at?: string;
  updated_at?: string;
  document_type?: { id: string; name: string };
  attachment?: { id: string; filename: string; content_type: string; file_size: number };
}

export interface DocumentCreatePayload {
  name: string;
  attachment_id: string;
  document_type_id: string;
}

// Zoom call capture (WIPE-1395)

// Someone who gains read access to a group's calls. The name is what the screen shows — an address
// is not what a person is recognised by — and the email comes along to tell two people with the
// same name apart. The backend falls back to the address when there is no user row to name.
export interface CallCaptureReader {
  email: string;
  name: string;
}

// A capture group the signed-in user records calls INTO. `enabled` is their own opt-in, and
// `authorized_users` is who gains access by it — named rather than counted, because the consent
// copy has to say who.
export interface CallCaptureSourceGroup {
  id: string;
  label: string;
  enabled: boolean;
  authorized_users: CallCaptureReader[];
}

// A capture group the user may READ through MCP. Overlaps the list above: a source is also a
// reader of their own group.
//
// No URL of its own (WIPE-1538): one endpoint serves every reader and works their groups out from
// their token, so this list only says what their Claude will be able to see.
export interface CallCaptureReadableGroup {
  id: string;
  label: string;
}

export interface CapturedCall {
  id: string;
  group_id: string;
  group_label: string;
  topic: string | null;
  started_at: string | null;
  duration_minutes: number | null;
  has_transcript: boolean;
  has_summary: boolean;
}

// Whether the user has installed the Zoom app on their own account. This is the consent that has
// force: until it is true, Zoom sends us nothing of theirs, which is why the group toggles below are
// refused without it. `zoom_email` is shown because it may not be the user's AgencyOS address, and
// seeing which account is attached is how they notice if it is the wrong one.
export interface ZoomConnection {
  connected: boolean;
  zoom_email: string | null;
  installed_at: string | null;
}

export interface CallCaptureSettings {
  zoom: ZoomConnection;
  sources: CallCaptureSourceGroup[];
  readable: CallCaptureReadableGroup[];
  // The connector to pick out of Claude's own list, named by the backend rather than written here so
  // it cannot drift from the `resource_name` the MCP server advertises. There is no URL to go with it:
  // the connector is admin-managed, so nobody configures one.
  connector_name: string;
  calls: CapturedCall[];
}

export const franchiseApi = new FranchiseApiClient();

// GET /v2/me/onboarding (WIPE-1645)
export type MyOnboardingStageState = 'complete' | 'current' | 'upcoming';

export interface MyOnboardingStage {
  key: string;
  title: string;
  /** One line on where the stage stands: "Signed · Sep 9", "Email & phone live · computer setup in progress". */
  detail: string;
  state: MyOnboardingStageState;
  /** 0–100. Shown as a figure only while the stage is current. */
  progress: number;
}

/** A WIPE-1143 meeting staff scheduled for the owner. */
export interface MyOnboardingMeeting {
  meeting_id: string;
  title: string;
  description: string;
  kind: 'meeting' | 'training';
  duration_minutes: number;
  /** ISO 8601 UTC. */
  starts_at: string;
  join_url: string | null;
}

export interface MyOnboarding {
  franchise_name: string | null;
  status: string;
  stages: MyOnboardingStage[];
  /** `upcoming` soonest first, `earlier` most recent first. */
  meetings: { upcoming: MyOnboardingMeeting[]; earlier: MyOnboardingMeeting[] };
}

export interface MyOnboardingResponse {
  onboarding: MyOnboarding | null;
}

// /v2/me/onboarding/messages (WIPE-1859)
export const MESSAGE_TEAMS = ['Account Management', 'Tech', 'Finance', 'Carrier Relations'] as const;
export type MessageTeam = (typeof MESSAGE_TEAMS)[number];

/**
 * awaiting_setup until IT setup is complete; awaiting_access until staff add the owner to the ticket;
 * ready with the ticket's customer-portal link; unavailable when HubSpot or Jira couldn't be asked.
 */
export type MyOnboardingMessagingState = 'awaiting_setup' | 'awaiting_access' | 'ready' | 'unavailable';

export interface MyOnboardingMessaging {
  state: MyOnboardingMessagingState;
  ticket?: { key: string; url: string };
}

export interface MyOnboardingMessagesResponse {
  messages: MyOnboardingMessaging | null;
}

export interface MyOnboardingMessageInput {
  team: MessageTeam;
  subject: string;
  message: string;
}

export interface MyOnboardingMessageSent {
  comment_id: string;
  ticket: { key: string; url: string };
}
